import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormHelperText from "@mui/material/FormHelperText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  MdChevronLeft,
  MdDeleteOutline,
  MdEdit,
  MdExpandMore,
  MdFactCheck,
  MdInfoOutline,
  MdLockOutline,
  MdMoreHoriz,
  MdWarningAmber,
} from "react-icons/md";
import { Banner, Progress, StatusChip } from "../mui";

/**
 * 工項詳情 — 一眼區（照抄設計稿 el-pm-template_4.html:627-648）
 *
 * 這一段的任務只有一個：**在第一個螢幕內看完**。
 * 型別與提醒各一個徽章、一行標題、一行「誰負責·什麼時候要交·哪個專案」、一條進度，
 * 動作只有兩顆。舊版把 15 個區塊平鋪在同一頁，使用者要自己找出「這件事現在怎樣」，
 * 那正是這次改版要拿掉的東西。
 *
 * 兩顆動作鈕都是外框鈕（outlined）：整頁唯一的實心主要鈕留給工作區的「送出回報」
 * （硬規則 1）。「回報進度」的作用只是捲到工作區，它不是這一頁真正的主要動作。
 *
 * 狀態轉換、編輯、刪除全部收進「⋯ 更多」，而且「不送審、直接結案」一定要先過
 * 確認視窗 —— 直接結案會讓審核人完全看不到這件事，那是回不去的後果（硬規則 4）。
 */

/**
 * 「不送審、直接結案」確認視窗 —— 文案逐字取自設計稿 el-pm-template_4.html:2632-2634。
 * 審核人姓名拿不到時就寫「審核人」，不編名字（硬規則 10）。
 * Esc 與點遮罩關閉由 MUI Dialog 負責；層級也由 Dialog 的 modal 層處理，
 * 塞在工項抽屜（TaskDrawer）裡時一樣會蓋在抽屜上面。
 */
function CloseConfirm({ reviewerName, busy, onCancel, onConfirm }) {
  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth role="alertdialog" aria-labelledby="task-close-title">
      <DialogTitle id="task-close-title" component="h2">
        確定不送審、直接結案？
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          直接結案後，{reviewerName}將不會收到此工項，也不會留下審核紀錄。若工作已完成，建議選擇「送出給審核人」。
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" onClick={onCancel}>
          取消
        </Button>
        <Button variant="contained" color="error" disabled={busy} loading={busy} loadingPosition="start" onClick={onConfirm}>
          確定直接結案
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CloseConfirm.propTypes = {
  reviewerName: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
CloseConfirm.defaultProps = { busy: false };

/** 一行排開、可換行的按鈕／文字列 */
const inlineSx = { flexWrap: "wrap", alignItems: "center" };

/** 轉換的目標狀態 → 中文（選單項目底下寫「改為『…』」，按之前就知道會變成什麼） */
const TARGET_LABEL = { pending: "待處理", progress: "進行中", review: "審核中", done: "已完成", closed: "已關閉" };

/**
 * 轉換的顯示名稱。後端工作流程的「取消」只有兩個字，看不出取消的是什麼，
 * 依目標狀態改成講得清楚的說法；其他名稱照呼叫端給的。
 */
function transLabel(t) {
  if (t.label === "取消") {
    if (t.target === "pending") return "取消進行（改回待處理）";
    if (t.target === "closed") return "取消此工項";
    return "取消此狀態";
  }
  return t.label;
}

function TaskGlance({
  onReview,
  embedded,
  readOnly,
  busy,
  typeLabel,
  tag,
  title,
  assigneeName,
  dueText,
  projectName,
  statusLabel,
  pct,
  progressTone,
  reviewerName,
  onBack,
  onReport,
  transitions,
  onTransition,
  onEdit,
  onDelete,
  pendingTrans,
  resolutions,
  pendingResId,
  onPickResolution,
  onConfirmPending,
  onCancelPending,
}) {
  /* 「更多」與確認視窗都是純畫面狀態，不必讓資料層知道 */
  /* 「更多」下拉選單的錨點；null＝關著 */
  const [moreEl, setMoreEl] = useState(null);
  const [confirmT, setConfirmT] = useState(null);
  const closeMore = () => setMoreEl(null);
  /* 選單項目：先關選單再做事，對話框與結論面板才不會被選單蓋住 */
  const pick = (fn) => () => {
    closeMore();
    fn();
  };

  const clickTransition = (t) => {
    if (t.locked || busy) return;
    // 直接結案是回不去的：先過確認視窗，文案講明白審核人看不到這件事
    if (t.confirmClose) {
      setConfirmT(t);
      return;
    }
    onTransition(t);
  };

  const hasMore = !readOnly && (transitions.length > 0 || onEdit || onDelete);

  return (
    <Card component="section">
      <CardContent sx={{ p: { xs: 3, md: 4 }, "&:last-child": { pb: { xs: 3, md: 4 } } }}>
        {/*
          版面：左邊是返回、狀態、標題、負責人與進度；右上角是動作鈕（回報進度、更多）。
          data-tour=det-flow：狀態流程的導覽指到動作鈕這一組（轉換鈕收在「更多」底下）。
          窄螢幕時右邊那組換行到下方。
        */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ flex: "1 1 420px", minWidth: 0 }}>
            {!embedded && onBack && (
              <Box sx={{ mb: 1.5 }}>
                <Button variant="text" startIcon={<MdChevronLeft aria-hidden="true" />} onClick={onBack}>
                  返回
                </Button>
              </Box>
            )}

            <Stack direction="row" spacing={1.5} useFlexGap sx={{ ...inlineSx, mb: 1.5 }}>
              {/* 型別徽章只有後端真的回了型別才貼 —— 認不得時一律退回「工作」，那比沒有徽章更誤導 */}
              {typeLabel && <StatusChip kind="type">{typeLabel}</StatusChip>}
              {tag && (
                <StatusChip kind={tag.kind} mark={tag.mark || null}>
                  {tag.text}
                </StatusChip>
              )}
            </Stack>

            {/* 內嵌時抽屜自己的標題列已經寫了標題，再寫一次是重複 */}
            {!embedded && (
              <Typography variant="h1" component="h1">
                {title}
              </Typography>
            )}

            {(assigneeName || dueText || projectName) && (
              <Typography sx={{ mt: 1.5 }}>
                {assigneeName && (
                  <>
                    <strong>{assigneeName}</strong> 負責
                  </>
                )}
                {assigneeName && (dueText || projectName) ? " · " : ""}
                {dueText}
                {dueText && projectName ? " · " : ""}
                {projectName}
              </Typography>
            )}

            {/* 寬度上限照設計稿：420px */}
            <Progress value={pct} tone={progressTone || "progress"} ariaLabel={`進度 ${pct}%`} sx={{ maxWidth: 420, mt: 2 }} />

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {!readOnly && hasMore ? "「送出給審核人」「不送審、直接結案」位於「更多」選單 · " : ""}
              目前狀態：{statusLabel}
            </Typography>
          </Box>

          {!readOnly && (onReview || onReport || hasMore) ? (
            <Stack data-tour="det-flow" direction="row" spacing={1.5} useFlexGap sx={{ flexShrink: 0, flexWrap: "wrap" }}>
              {/* 待我審核時主要動作是「開始審核」，不是回報進度 */}
              {onReview ? (
                <Button variant="contained" startIcon={<MdFactCheck aria-hidden="true" />} onClick={onReview}>
                  開始審核
                </Button>
              ) : null}
              {!onReview && onReport && (
                <Button variant="outlined" onClick={onReport}>
                  回報進度
                </Button>
              )}
              {hasMore && (
                <Button
                  variant="outlined"
                  aria-haspopup="menu"
                  aria-expanded={moreEl ? true : undefined}
                  aria-controls={moreEl ? "task-more-menu" : undefined}
                  startIcon={<MdMoreHoriz aria-hidden="true" />}
                  endIcon={<MdExpandMore aria-hidden="true" />}
                  onClick={(e) => setMoreEl(e.currentTarget)}
                >
                  更多
                </Button>
              )}
            </Stack>
          ) : null}
        </Box>

        {/* 唯讀（審核抽屜）要講清楚為什麼這裡沒有任何按鈕，不然使用者只會以為壞了 */}
        {readOnly && (
          <Banner kind="info" icon={<MdInfoOutline />} sx={{ mt: 2 }}>
            此為唯讀檢視，僅供審核參考，任何操作皆不會變更工項。
          </Banner>
        )}

        {/*
          「更多」下拉：狀態轉換、編輯、刪除。鎖住的轉換照樣列出（不可點），原因寫在項目底下 ——
          整個藏起來的話，使用者不會知道還有這個動作、也不知道差什麼才能做。
        */}
        {!readOnly && hasMore && (
          <Menu
            id="task-more-menu"
            anchorEl={moreEl}
            open={!!moreEl}
            onClose={closeMore}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { minWidth: 260, maxWidth: 400 } } }}
          >
            {transitions.map((t) => {
              const label = transLabel(t);
              const to = TARGET_LABEL[t.target];
              const reason = String(t.reason || "").replace(/把關者/g, "審核人");
              /* 鎖住的轉換照樣列出、但點了沒反應；原因放 tooltip（滑過或鍵盤聚焦時出現），旁邊放鎖頭提示有原因可看 */
              const locked = t.locked || busy;
              const item = (
                <MenuItem
                  key={t.key}
                  aria-disabled={locked ? "true" : undefined}
                  onClick={locked ? undefined : pick(() => clickTransition(t))}
                  sx={locked ? { cursor: "not-allowed", "&:hover": { bgcolor: "transparent" } } : undefined}
                >
                  <ListItemText
                    primary={label}
                    secondary={to ? `改為「${to}」` : null}
                    slotProps={{ primary: { sx: { color: locked ? "text.disabled" : "text.primary" } } }}
                  />
                  {t.locked ? (
                    <Box component="span" aria-hidden="true" sx={{ display: "flex", ml: 2, color: "text.disabled" }}>
                      <MdLockOutline />
                    </Box>
                  ) : null}
                </MenuItem>
              );
              return t.locked && reason ? (
                <Tooltip key={t.key} title={reason} placement="left" describeChild>
                  {item}
                </Tooltip>
              ) : (
                item
              );
            })}
            {transitions.length > 0 && (onEdit || onDelete) ? <Divider /> : null}
            {onEdit ? (
              <MenuItem onClick={pick(onEdit)}>
                <ListItemIcon>
                  <MdEdit />
                </ListItemIcon>
                編輯工項
              </MenuItem>
            ) : null}
            {onDelete ? (
              <MenuItem onClick={pick(onDelete)} sx={{ color: "error.main" }}>
                <ListItemIcon sx={{ color: "inherit" }}>
                  <MdDeleteOutline />
                </ListItemIcon>
                刪除工項
              </MenuItem>
            ) : null}
          </Menu>
        )}

        {/*
          需要解決結論的轉換（關閉之類）：選單選了之後在這裡選結論再確認。
          也可能是從工作區的「送出回報」觸發（quickReport 會把待確認的轉換丟回來），
          所以這一塊只看 pendingTrans，不跟選單的開關綁在一起。
          結論會進報表（重開率、不予修復比例…），所以讓人選而不是系統代填。
        */}
        {!readOnly && pendingTrans && (
          <Stack spacing={1.5} sx={{ mt: 3, p: 2, borderRadius: "var(--radius-md)", bgcolor: "action.hover" }}>
            <TextField
              select
              id="task-resolution"
              label={`「${pendingTrans.label}」的解決結論`}
              value={pendingResId}
              onChange={(e) => onPickResolution(e.target.value)}
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              sx={{ maxWidth: 420 }}
            >
              {resolutions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </TextField>
            {resolutions.length === 0 && (
              <FormHelperText error sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <MdWarningAmber aria-hidden="true" />
                系統尚未設定解決結論選項，請聯絡管理者新增。
              </FormHelperText>
            )}
            <Stack direction="row" spacing={1.5} useFlexGap sx={inlineSx}>
              <Button variant="contained" disabled={busy || !pendingResId} loading={busy} loadingPosition="start" onClick={onConfirmPending}>
                確認{pendingTrans.label}
              </Button>
              <Button variant="text" onClick={onCancelPending}>
                取消
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>

      {confirmT && (
        <CloseConfirm
          reviewerName={reviewerName}
          busy={busy}
          onCancel={() => setConfirmT(null)}
          onConfirm={() => {
            const t = confirmT;
            setConfirmT(null);
            onTransition(t);
          }}
        />
      )}
    </Card>
  );
}

TaskGlance.propTypes = {
  /** 有值＝目前使用者是這一輪尚未表態的審核人：右上角主要按鈕換成「開始審核」 */
  onReview: PropTypes.func,
  embedded: PropTypes.bool,
  readOnly: PropTypes.bool,
  busy: PropTypes.bool,
  /** 型別白話名；後端沒回型別時是空字串，那就不貼徽章 */
  typeLabel: PropTypes.string,
  /** 提醒徽章（今天要交／已逾期／狀態），規則與 Today 的 tagOf 同一套 */
  tag: PropTypes.shape({
    /** StatusChip 的 kind（attention / warn / progress / done / closed） */
    kind: PropTypes.string,
    mark: PropTypes.node,
    text: PropTypes.string,
  }),
  title: PropTypes.string,
  assigneeName: PropTypes.string,
  dueText: PropTypes.string,
  projectName: PropTypes.string,
  statusLabel: PropTypes.string,
  pct: PropTypes.number,
  /** 進度條顏色（Progress 的 tone）；空字串＝一般進行中 */
  progressTone: PropTypes.string,
  /** 確認視窗要講「誰看不到這件事」；拿不到姓名時傳「審核人」 */
  reviewerName: PropTypes.string,
  onBack: PropTypes.func,
  onReport: PropTypes.func,
  transitions: PropTypes.array,
  onTransition: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  pendingTrans: PropTypes.object,
  resolutions: PropTypes.array,
  pendingResId: PropTypes.string,
  onPickResolution: PropTypes.func,
  onConfirmPending: PropTypes.func,
  onCancelPending: PropTypes.func,
};

TaskGlance.defaultProps = {
  onReview: null,
  embedded: false,
  readOnly: false,
  busy: false,
  typeLabel: "",
  tag: null,
  title: "",
  assigneeName: "",
  dueText: "",
  projectName: "",
  statusLabel: "",
  pct: 0,
  progressTone: "",
  reviewerName: "審核人",
  onBack: null,
  onReport: null,
  transitions: [],
  onTransition: () => {},
  onEdit: null,
  onDelete: null,
  pendingTrans: null,
  resolutions: [],
  pendingResId: "",
  onPickResolution: () => {},
  onConfirmPending: () => {},
  onCancelPending: () => {},
};

export default TaskGlance;
